/**
 * Pure helpers for the AI-vs-consultant rule-suggestion nudge.
 *
 * Mirrors the backend classifier in services/api/genui/rule_suggestions.py:
 * `approved` is one pole; everything that flags a problem (`rejected` /
 * `partial-approval`) is the other. A reject-vs-partial pair is therefore
 * agreement — both see a problem. `pending-data` / `reset` / null never count.
 */

export type ConsultantDecision = 'approved' | 'rejected' | 'partial-approval';

const KNOWN = new Set(['approved', 'rejected', 'partial-approval']);
const PROBLEM = new Set(['rejected', 'partial-approval']);

/** True when the consultant's decision and the AI recommendation are on opposite poles. */
export function isRuleSuggestionDisagreement(
  consultant: string | null | undefined,
  ai: string | null | undefined,
): boolean {
  if (!consultant || !ai || !KNOWN.has(consultant) || !KNOWN.has(ai)) return false;
  return PROBLEM.has(consultant) !== PROBLEM.has(ai);
}

const DECISION_VERB: Record<ConsultantDecision, string> = {
  approved: 'approved',
  rejected: 'rejected',
  'partial-approval': 'partially approved',
};

/** Seed text handed to the Otto chat so the agent can draft contract review rules. */
export function buildRuleSuggestionMessage(args: {
  invoiceNumber: string;
  propertyName: string;
  invoiceId: string;
  consultant: ConsultantDecision;
  aiStatus: string;
  reason?: string;
  rejectionCategory?: string[];
}): string {
  const head = `I just ${DECISION_VERB[args.consultant]} invoice #${args.invoiceNumber} for ${args.propertyName}, but Lift AI recommended "${args.aiStatus}".`;
  const tail = `(invoice id: ${args.invoiceId}; my decision: ${args.consultant})`;
  const reason = args.reason?.trim();

  // No recorded reason — typically an approval override, where the consultant
  // gives no rejection reason or category. Don't draft rules from empty
  // context; have the agent ask what Lift AI got wrong first. (Including a
  // blank "category/reason" here would only mislead the agent.)
  if (!reason) {
    return [
      head,
      `I haven't recorded why I disagree — please ask me what Lift AI got wrong, then suggest contract review rules so it gets this right next time.`,
      tail,
    ].join(' ');
  }

  const categories = args.rejectionCategory?.length
    ? args.rejectionCategory.join(', ')
    : 'none specified';
  return [
    head,
    `My category: ${categories}. My reason: "${reason}".`,
    `Please suggest contract review rules so Lift AI gets this right next time.`,
    tail,
  ].join(' ');
}
